<template>
  <div class="title-plate">
    <div class="top-sequence">
      <div class="title-and-kebob">
        <div class="title-and-pill">
          <h3 class="title">
            {{ model.title.trim()
            }}<span class="pill">{{ privacyLanguage }}</span>
          </h3>
        </div>
        <div class="kebob">
          <h-control-dock :type="type" :id="model.id" :actions="actions" />
        </div>
      </div>
      <h-developer
        :id="model.developerID"
        v-slot="{ dev_link, user }"
        tag="span"
      >
        <div class="level developer">
          <figure class="image is-24x24">
            <img
              :src="cloud(user.profilePic)"
              alt="Placeholder image"
              class="is-rounded"
              @error="imageUrlAlt"
            />
          </figure>
          <span>
            <component :is="dev_link" />
          </span>
        </div>
      </h-developer>
      <div class="vertical-center">
        <div class="is-pulled-left" v-if="roundedRating">
          <b-rate
            v-model="roundedRating"
            icon="circle"
            size="is-small"
            :spaced="true"
            :disabled="true"
          ></b-rate>
        </div>
        <div class="collectStatus">
          Collected {{ model.downloadCount || 0 }} time{{
            model.downloadCount !== 1 ? `s` : ``
          }}
        </div>
      </div>
    </div>
    <div class="buttons modal-buttons">
      <b-button
        v-if="actions.includes(`collect`)"
        size="is-large"
        :class="inCollection ? 'is-primary' : 'is-success'"
        @click="openModal"
        :disabled="inCollection || !$can($A.POST, $R.FAVORITES)"
        >{{ inCollection ? "In Collection" : "Add to Favorites" }}</b-button
      >
      <add-to-space v-if="actions.includes(`place`)" size="is-large" />
    </div>
    <div class="description">
      <p>
        <h-read-more
          missing-str="No description provided."
          :max-chars="200"
          :text="model.description"
        />
      </p>
    </div>
    <div class="taglist">
      <h-split-tag
        class="control"
        type="is-light"
        textLeft="Template"
        :textRight="model.template"
        v-if="model.template"
      >
      </h-split-tag>
      <h-split-tag
        class="control"
        type="is-light"
        textLeft="License"
        :textRight="model.license[0]"
        v-if="model.license && model.license[0]"
      >
      </h-split-tag>
      <h-split-tag
        class="control"
        type="is-light"
        textLeft="Provider"
        :textRight="model.provider[0]"
        v-if="model.provider && model.provider[0]"
      >
      </h-split-tag>
      <b-tag v-for="(tag, i) in model.tags" :key="tag + i" ellipsis>{{
        tag
      }}</b-tag>
    </div>
  </div>
</template>

<script>
import HControlDock from "@/components/spaces/HControlDock.modal";
import AddToSpace from "@/components/spaces/Modal.AddToSpace";
import { mapGetters } from "vuex";
import { showModal } from "@/common/modal.service.js";
import FavoritesNew from "@/views/Favorites/FavoritesNew";

const buttonMap = {
  spaces: ["vr", "collect"],
  models: ["collect", "place"],
  webapps: ["collect", "place"],
  texts: ["place"],
  videos: ["place"],
  audios: ["place"],
  pictures: ["place"],
};

export default {
  components: { HControlDock, AddToSpace },
  props: ["model", "privateType", "type"],
  data() {
    return {
      inCollection: false,
    };
  },
  methods: {
    imageUrlAlt(event) {
      event.target.src = this.cloud(`avatar.png`);
    },
    openModal() {
      showModal({
        props: {
          resourceID: this.model.id,
          type: this.type,
        },
        overflow: true,
        component: FavoritesNew,
        hasModalCard: true,
        parent: this,
        onCancel: e => console.log("Method of cancel: ", e),
        events: {
          close: e => console.log("Closed via button: ", e),
        },
      });
    },
  },
  computed: {
    ...mapGetters(["currentUser"]),
    actions() {
      return buttonMap[
        this.privateType ? this.type.split("t_").pop() : this.type
      ];
    },
    privacyLanguage() {
      // If it's t_content and public=true, Show that it's pending approval
      if (this.privateType && this.model.public) return "Pending Approval";
      // If it's t_content and public=false, no need to show more information bc it's simply private
      if (this.privateType && !this.model.public) return "Private";
      // If it's regular content, no need to show more info bc it's simply public
      else return "Public";
    },
    roundedRating() {
      const rating = 4.3; //This should say this.model.rating when we implement ratings
      return rating && Math.floor(rating);
    },
  },
  async created() {
    this.inCollection = await this.model.$inCollection();
  },
  watch: {
    currentUser: {
      async handler() {
        this.inCollection = await this.model.$inCollection();
      },
      deep: true,
    },
    async model() {
      this.inCollection = await this.model.$inCollection();
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/bulma_overrides.scss";

.modal-buttons {
  margin-right: auto;
  margin-left: 0px;
}

.title-and-pill {
  display: flex;
  align-items: center;
}

.title {
  max-width: 100%;
  margin-bottom: 0px;
}

.pill {
  padding: 3px 5px;
  border: 1px solid var(--grey);
  border-radius: $radius;
  color: var(--grey);
  font-weight: 400;
  position: relative;
  top: -3px; // move it up 3px for more pleasing vertical centering
  font-size: 0.8rem;
  line-height: 2rem;
  margin-left: 10px;
}
//Dont shift pill up quite so much if we're in animation-content (modal mode), bc introduces poor alignment with smaller title-font
.animation-content .pill {
  top: -1px;
}

.taglist {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  > [class^="tag"]:not(:last-child) {
    margin-right: 10px;
    margin-bottom: 10px;
  }
}
.title-plate {
  display: grid;
  grid-row-gap: 20px;
}

.title-plate .image {
  display: inline-block;
}
.title-and-kebob {
  display: flex;
  & .kebob {
    max-width: min-content;
    margin-left: auto;
    flex-grow: 1;
    padding-left: 5px;
    height: 10px; // small height only so doesnt increase title's height
  }
}

.vertical-center {
  display: grid;
  grid-template-columns: min-content 1fr;
}

.vertical-center .rate {
  height: 100%;
  margin-right: 10px;
}

.developer {
  white-space: pre;
  width: min-content;
  > span {
    margin-left: 5px;
  }
}

.developer {
  margin-bottom: 5px;
}

.title-plate .buttons {
  margin-bottom: 0px;
}

.rate-item .icon svg.feather {
  fill: currentColor;
}
</style>
